import React, { useState, useEffect, useRef } from 'react';
import { timelineAPI, reportsAPI, gapsAPI } from '../../services/api';
import EmployeeTraining from '../Training/EmployeeTraining';
import { fmtDate } from '../../utils/dateUtils';

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL  = ['January','February','March','April','May','June','July','August','September','October','November','December'];

/* ── Gap detection ───────────────────────────────────────────────────────────── */
function detectGaps(entries, joiningDate) {
  const sorted = [...entries].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  const gaps = [];

  // Gap between joining date and first timeline entry
  if (joiningDate && sorted.length > 0) {
    const joinMs      = new Date(joiningDate).getTime();
    const firstStart  = new Date(sorted[0].start_date).getTime();
    if (firstStart - joinMs > 86400000) {
      gaps.push({
        gapStart:      joiningDate,
        gapEnd:        new Date(firstStart - 86400000).toISOString().slice(0, 10),
        afterEntryId:  null,
        beforeEntryId: sorted[0].id,
        isJoiningGap:  true,
      });
    }
  }

  // Gaps between consecutive entries
  for (let i = 0; i < sorted.length - 1; i++) {
    const curr = sorted[i];
    const next = sorted[i + 1];
    if (!curr.end_date) continue;
    const endMs   = new Date(curr.end_date).getTime();
    const startMs = new Date(next.start_date).getTime();
    if (startMs - endMs > 86400000) {
      gaps.push({
        gapStart:      new Date(endMs + 86400000).toISOString().slice(0, 10),
        gapEnd:        new Date(startMs - 86400000).toISOString().slice(0, 10),
        afterEntryId:  curr.id,
        beforeEntryId: next.id,
        isJoiningGap:  false,
      });
    }
  }
  return gaps;
}

/* ── Find timeline entry covering a given month ─────────────────────────────── */
function getEntryForMonth(year, month, entries) {
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd   = new Date(year, month, 0);
  return entries.find((e) => {
    const s = new Date(e.start_date);
    const en = e.end_date ? new Date(e.end_date) : new Date();
    return s <= monthEnd && en >= monthStart;
  }) || null;
}

/* ══════════════════════════════════════════════════════════════════════════════
   Main component
══════════════════════════════════════════════════════════════════════════════ */
export default function WorkHistory({ employeeId, employee }) {
  const [timeline,    setTimeline]    = useState([]);
  const [reports,     setReports]     = useState({});
  const [gaps,        setGaps]        = useState([]);
  const [joiningDate, setJoiningDate] = useState(null); // from backend
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [panel,       setPanel]       = useState('status'); // 'status' | 'timeline'

  const currentYear  = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const joinYear  = joiningDate ? new Date(joiningDate).getFullYear()    : currentYear - 2;
  const joinMonth = joiningDate ? new Date(joiningDate).getMonth() + 1   : 1;

  const yearRange = Array.from({ length: currentYear - joinYear + 1 }, (_, i) => joinYear + i).reverse();

  const [selectedYear, setSelectedYear] = useState(currentYear);

  function loadData() {
    setLoading(true);
    Promise.all([
      timelineAPI.list(employeeId),
      reportsAPI.list(employeeId),
      gapsAPI.list(employeeId),
    ])
      .then(([tRes, rRes, gRes]) => {
        setTimeline(tRes.data.data.entries       || []);
        setReports(rRes.data.data.reports        || {});
        setGaps(gRes.data.data.gaps              || []);
        setJoiningDate(tRes.data.data.joining_date || null);
      })
      .catch(() => setError('Failed to load work history.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadData(); }, [employeeId]); // eslint-disable-line

  const detectedGaps = detectGaps(timeline, joiningDate);

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
      <div className="w-5 h-5 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin mr-3" />
      Loading work history…
    </div>
  );
  if (error) return <div className="text-center py-10 text-red-600">{error}</div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* ── Inner tab chips ────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100 bg-slate-50/60">
        {[
          { key: 'status',   label: 'Status Report',  count: Object.keys(reports).length },
          { key: 'timeline', label: 'Job Timeline',   count: timeline.length },
          { key: 'training', label: 'Training',       count: null },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setPanel(t.key)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${
              panel === t.key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400 hover:text-blue-600'
            }`}>
            {t.label}
            {t.count !== null && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                panel === t.key ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-500'
              }`}>
                {t.count}
              </span>
            )}
          </button>
        ))}

        {/* Gap warning badge */}
        {detectedGaps.some((g) => !gaps.find((s) => s.gap_start === g.gapStart && s.gap_end === g.gapEnd)) && (
          <button
            onClick={() => setPanel('timeline')}
            className="ml-auto flex items-center gap-1.5 text-xs text-orange-600 font-semibold bg-orange-50 border border-orange-200 px-3 py-1 rounded-full hover:bg-orange-100">
            ⚠ Gap{detectedGaps.length > 1 ? 's' : ''} need reason
          </button>
        )}
      </div>

      {/* ── Status Report panel ────────────────────────────────────────────────── */}
      {panel === 'status' && (
        <div>
          {/* Year chips */}
          <div className="px-5 py-3 border-b border-slate-100 flex gap-2 flex-wrap">
            {yearRange.map((y) => (
              <button
                key={y}
                onClick={() => setSelectedYear(y)}
                className={`px-3.5 py-1 rounded-full text-xs font-semibold transition-all ${
                  selectedYear === y
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}>
                {y}
              </button>
            ))}
          </div>

          {/* Monthly cards — one per row */}
          <div className="divide-y divide-slate-100">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
              const isFuture = selectedYear > currentYear ||
                (selectedYear === currentYear && month > currentMonth);
              const isBeforeJoining = selectedYear < joinYear ||
                (selectedYear === joinYear && month < joinMonth);
              return (
                <MonthCard
                  key={month}
                  employeeId={employeeId}
                  year={selectedYear}
                  month={month}
                  report={reports[`${selectedYear}_${month}`] || null}
                  isFuture={isFuture}
                  isBeforeJoining={isBeforeJoining}
                  timeline={timeline}
                  onSaved={loadData}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* ── Job Timeline panel ─────────────────────────────────────────────────── */}
      {panel === 'timeline' && (
        <JobTimelinePanel
          employeeId={employeeId}
          timeline={timeline}
          detectedGaps={detectedGaps}
          savedGaps={gaps}
          onSaved={loadData}
        />
      )}

      {/* ── Training panel ─────────────────────────────────────────────────────── */}
      {panel === 'training' && (
        <div className="p-5">
          <EmployeeTraining employeeId={employeeId} />
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Month Card — one full-width row per month
══════════════════════════════════════════════════════════════════════════════ */
function MonthCard({ employeeId, year, month, report, isFuture, isBeforeJoining, timeline, onSaved }) {
  const [expanded, setExpanded] = useState(false);
  const [form,     setForm]     = useState({});
  const [file,     setFile]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [err,      setErr]      = useState('');
  const [success,  setSuccess]  = useState('');
  const fileRef = useRef();

  function openForm() {
    if (isFuture || isBeforeJoining) return;
    const entry = getEntryForMonth(year, month, timeline);
    setForm({
      role:            report?.role            ?? entry?.role         ?? '',
      client:          report?.client          ?? entry?.client       ?? '',
      technology:      report?.technology      ?? entry?.technology   ?? '',
      vendor:          report?.vendor          ?? entry?.vendor       ?? '',
      description:     report?.description     ?? '',
      tools:           report?.tools           ?? '',
      i983_change:     report ? String(report.i983_change)    === '1' : false,
      issues:          report?.issues          ?? '',
      supervisor_call: report ? String(report.supervisor_call) === '1' : false,
    });
    setFile(null); setErr(''); setSuccess('');
    setExpanded(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setErr(''); setSuccess('');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('employee_id',     String(employeeId));
      fd.append('report_year',     String(year));
      fd.append('report_month',    String(month));
      fd.append('role',            form.role            || '');
      fd.append('client',          form.client          || '');
      fd.append('technology',      form.technology      || '');
      fd.append('vendor',          form.vendor          || '');
      fd.append('description',     form.description     || '');
      fd.append('tools',           form.tools           || '');
      fd.append('i983_change',     form.i983_change     ? '1' : '0');
      fd.append('issues',          form.issues          || '');
      fd.append('supervisor_call', form.supervisor_call ? '1' : '0');
      if (file) fd.append('report_file', file);
      await reportsAPI.save(fd);
      setSuccess('Saved!');
      if (fileRef.current) fileRef.current.value = '';
      setFile(null);
      setTimeout(() => { setSuccess(''); setExpanded(false); onSaved(); }, 1400);
    } catch (ex) {
      setErr(ex.response?.data?.message || 'Save failed.');
    } finally {
      setLoading(false);
    }
  }

  function sf(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  const isFilled   = !!report;
  // Green only when required fields are set AND a file is uploaded
  const isComplete = !!(report && report.file_path && report.role && report.technology);
  const hasI983    = report?.i983_change    === 1 || report?.i983_change    === '1';
  const hasIssues  = !!(report?.issues && report.issues.trim());
  const hasCall    = report?.supervisor_call === 1 || report?.supervisor_call === '1';

  const isDisabled = isFuture || isBeforeJoining;

  return (
    <div className={isDisabled ? 'opacity-45' : ''}>

      {/* ── Collapsed row ── */}
      <div
        onClick={() => !expanded && openForm()}
        className={`flex items-center gap-4 px-5 py-3.5 select-none ${
          isBeforeJoining ? 'bg-slate-50/80' : ''
        } ${!isDisabled && !expanded ? 'cursor-pointer hover:bg-slate-50' : ''} ${
          expanded ? 'bg-blue-50/40' : ''
        }`}>

        {/* Status dot + month name */}
        <div className="flex items-center gap-2.5 w-28 shrink-0">
          <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
            isDisabled  ? 'bg-slate-200'
            : isComplete ? 'bg-green-400'
            : 'bg-slate-300'
          }`} />
          <span className={`text-sm font-semibold ${isDisabled ? 'text-slate-400' : 'text-slate-700'}`}>
            {MONTHS_FULL[month - 1]}
          </span>
        </div>

        {/* Summary */}
        <div className="flex-1 flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0">
          {isBeforeJoining ? (
            <span className="text-xs text-slate-400 italic">Before joining</span>
          ) : isFuture ? null : isComplete ? (
            <>
              {report.role   && <span className="text-sm text-slate-700 font-medium truncate">{report.role}</span>}
              {report.client && <span className="text-sm text-slate-500 truncate">{report.client}</span>}
              {report.technology && (
                <span className="text-xs text-slate-400 truncate hidden sm:block">{report.technology}</span>
              )}
              <div className="flex gap-1.5 shrink-0">
                {hasI983   && <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">I-983</span>}
                {hasIssues && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">Issues</span>}
                {hasCall   && <span className="text-xs bg-blue-100   text-blue-700   px-2 py-0.5 rounded-full font-medium">Call</span>}
              </div>
            </>
          ) : (
            <span className="text-sm text-orange-400 italic">Report not uploaded</span>
          )}
        </div>

        {/* Action */}
        {!isDisabled && (
          <div className="shrink-0 ml-auto">
            {expanded ? (
              <span className="text-xs text-slate-400">▲</span>
            ) : isFilled ? (
              <span className="text-xs text-slate-400 border border-slate-200 px-2.5 py-1 rounded-lg hover:bg-slate-100">Edit</span>
            ) : (
              <span className="text-xs text-blue-500 border border-blue-200 px-2.5 py-1 rounded-lg hover:bg-blue-50">+ Add</span>
            )}
          </div>
        )}
      </div>

      {/* ── Expanded form ── */}
      {expanded && (
        <form onSubmit={handleSave} className="px-5 pb-5 pt-3 bg-slate-50/60 border-t border-slate-100 space-y-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            {MONTHS_FULL[month - 1]} {year}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { key: 'role',       label: 'Role',       ph: 'Software Engineer' },
              { key: 'client',     label: 'Client',     ph: 'Client name' },
              { key: 'technology', label: 'Technology', ph: 'React, Node.js…' },
              { key: 'vendor',     label: 'Vendor',     ph: 'Staffing vendor' },
              { key: 'tools',      label: 'Tools',      ph: 'Tools used' },
            ].map(({ key, label, ph }) => (
              <div key={key}>
                <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
                <input value={form[key] || ''} onChange={(e) => sf(key, e.target.value)}
                  placeholder={ph} className="input-base" />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Description</label>
              <textarea value={form.description || ''} onChange={(e) => sf('description', e.target.value)}
                rows={3} placeholder="Work description" className="input-base resize-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Issues</label>
              <textarea value={form.issues || ''} onChange={(e) => sf('issues', e.target.value)}
                rows={3} placeholder="Any issues this month" className="input-base resize-none" />
            </div>
          </div>

          <div className="flex flex-wrap gap-5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.i983_change}
                onChange={(e) => sf('i983_change', e.target.checked)}
                className="w-4 h-4 accent-violet-600" />
              <span className="text-sm text-slate-700">I-983 change this month</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.supervisor_call}
                onChange={(e) => sf('supervisor_call', e.target.checked)}
                className="w-4 h-4 accent-blue-600" />
              <span className="text-sm text-slate-700">Supervisor call completed</span>
            </label>
          </div>

          {/* File */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Report File
              {report?.file_path && (
                <a href={reportsAPI.previewUrl(report.id)} target="_blank" rel="noreferrer"
                  className="ml-2 text-blue-600 hover:underline normal-case font-normal">
                  View existing
                </a>
              )}
            </label>
            <div className="border border-dashed border-slate-300 rounded-lg px-4 py-3 text-center cursor-pointer bg-white hover:bg-slate-50"
              onClick={() => fileRef.current?.click()}>
              {file
                ? <span className="text-sm text-blue-600 font-medium">{file.name}</span>
                : <span className="text-sm text-slate-400">{report?.file_path ? 'Replace file…' : 'Click to attach PDF / image'}</span>}
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
              onChange={(e) => setFile(e.target.files[0] || null)} />
          </div>

          {err     && <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{err}</p>}
          {success && <p className="text-green-600 text-sm bg-green-50 border border-green-200 px-3 py-2 rounded-lg">{success}</p>}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={() => setExpanded(false)}
              className="flex-1 border border-slate-300 text-slate-600 py-2.5 rounded-lg text-sm hover:bg-white">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
              {loading
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
                : isFilled ? 'Update Report' : 'Save Report'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Job Timeline Panel
══════════════════════════════════════════════════════════════════════════════ */
function JobTimelinePanel({ employeeId, timeline, detectedGaps, savedGaps, onSaved }) {
  const [showForm,      setShowForm]      = useState(false);
  const [editEntry,     setEditEntry]     = useState(null);
  const [gapForms,      setGapForms]      = useState({});
  const [gapSaving,     setGapSaving]     = useState({});
  const [loading,       setLoading]       = useState(false);
  const [err,           setErr]           = useState('');
  const [deleteTarget,  setDeleteTarget]  = useState(null); // entry id
  const [deleteLoading, setDeleteLoading] = useState(false);

  const blank = { role:'', technology:'', client:'', vendor:'', start_date:'', end_date:'', is_current:false, notes:'' };
  const [form, setForm] = useState(blank);

  function startAdd()      { setForm(blank); setEditEntry(null); setErr(''); setShowForm(true); }
  function startEdit(entry) {
    setForm({
      role:        entry.role        || '',
      technology:  entry.technology  || '',
      client:      entry.client      || '',
      vendor:      entry.vendor      || '',
      start_date:  entry.start_date  || '',
      end_date:    entry.end_date    || '',
      is_current:  Number(entry.is_current) === 1,
      notes:       entry.notes       || '',
    });
    setEditEntry(entry); setErr(''); setShowForm(true);
  }
  function cancelForm() { setShowForm(false); setEditEntry(null); setErr(''); }
  function sf(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await timelineAPI.delete(deleteTarget);
      setDeleteTarget(null);
      onSaved();
    } catch (ex) {
      alert(ex.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setErr('');
    if (!form.technology.trim())            { setErr('Technology is required.'); return; }
    if (!form.role.trim())                  { setErr('Role is required.'); return; }
    if (!form.start_date)                   { setErr('Start date is required.'); return; }
    if (!form.is_current && !form.end_date) { setErr('End date is required when not currently working.'); return; }
    setLoading(true);
    try {
      await timelineAPI.save({
        id:          editEntry ? editEntry.id : 0,
        employee_id: employeeId,
        technology:  form.technology,
        role:        form.role,
        client:      form.client,
        vendor:      form.vendor,
        start_date:  form.start_date,
        end_date:    form.is_current ? null : (form.end_date || null),
        is_current:  form.is_current,
        notes:       form.notes,
      });
      cancelForm();
      onSaved();
    } catch (ex) {
      setErr(ex.response?.data?.message || 'Save failed.');
    } finally {
      setLoading(false);
    }
  }

  async function saveGapReason(gapStart, gapEnd) {
    const key = `${gapStart}_${gapEnd}`;
    const reason = gapForms[key] || '';
    if (!reason.trim()) return;
    setGapSaving((p) => ({ ...p, [key]: true }));
    try {
      await gapsAPI.save({ employee_id: employeeId, gap_start: gapStart, gap_end: gapEnd, reason });
      onSaved();
    } finally {
      setGapSaving((p) => ({ ...p, [key]: false }));
    }
  }

  function savedGapReason(gs, ge) {
    return savedGaps.find((g) => g.gap_start === gs && g.gap_end === ge)?.reason || '';
  }

  const sorted = [...timeline].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

  return (
    <div>
      {/* Toolbar */}
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <span className="text-xs text-slate-400">
          {timeline.length === 0 ? 'No entries yet' : `${timeline.length} entr${timeline.length === 1 ? 'y' : 'ies'}`}
        </span>
        {!showForm && (
          <button onClick={startAdd}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-1.5 rounded-lg text-xs font-semibold">
            + Add Entry
          </button>
        )}
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <form onSubmit={handleSave} className="px-5 py-4 space-y-3 bg-slate-50/50 border-b border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            {editEntry ? 'Edit Entry' : 'New Timeline Entry'}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TLField label="Technology" required>
              <input value={form.technology} onChange={(e) => sf('technology', e.target.value)}
                className="input-base" placeholder="e.g. Java, React, Python" />
            </TLField>
            <TLField label="Role" required>
              <input value={form.role} onChange={(e) => sf('role', e.target.value)}
                className="input-base" placeholder="Job title / position" />
            </TLField>
            <TLField label="Client">
              <input value={form.client} onChange={(e) => sf('client', e.target.value)}
                className="input-base" placeholder="End client" />
            </TLField>
            <TLField label="Vendor">
              <input value={form.vendor} onChange={(e) => sf('vendor', e.target.value)}
                className="input-base" placeholder="Staffing vendor" />
            </TLField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TLField label="Start Date" required>
              <input type="date" value={form.start_date} onChange={(e) => sf('start_date', e.target.value)}
                className="input-base" />
            </TLField>
            {!form.is_current && (
              <TLField label="End Date" required>
                <input type="date" value={form.end_date} onChange={(e) => sf('end_date', e.target.value)}
                  min={form.start_date || undefined} className="input-base" />
              </TLField>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_current}
              onChange={(e) => { sf('is_current', e.target.checked); if (e.target.checked) sf('end_date', ''); }}
              className="w-4 h-4 accent-blue-600" />
            <span className="text-sm text-slate-700">Currently working here</span>
          </label>

          <TLField label="Notes">
            <textarea value={form.notes} onChange={(e) => sf('notes', e.target.value)}
              rows={2} className="input-base resize-none" placeholder="Optional notes" />
          </TLField>

          {err && <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{err}</p>}

          <div className="flex gap-2">
            <button type="button" onClick={cancelForm}
              className="flex-1 border border-slate-300 text-slate-600 py-2 rounded-lg text-sm hover:bg-slate-100">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
              {loading
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
                : editEntry ? 'Update' : 'Save Entry'}
            </button>
          </div>
        </form>
      )}

      {/* Empty state */}
      {timeline.length === 0 && !showForm && (
        <div className="px-5 py-12 text-center text-slate-400 text-sm">
          No job entries yet.
          <br />
          <button onClick={startAdd} className="mt-2 text-blue-600 hover:underline font-medium">
            Add the first entry
          </button>
        </div>
      )}

      {/* Timeline list */}
      {sorted.length > 0 && (
        <div className="divide-y divide-slate-100">
          {sorted.map((entry, idx) => {
            const gapAfter    = detectedGaps.find((g) => g.afterEntryId === entry.id && !g.isJoiningGap);
            const gKey        = gapAfter ? `${gapAfter.gapStart}_${gapAfter.gapEnd}` : null;
            // The joining-date gap appears after the oldest (last in sorted-desc) entry
            const joiningGap  = idx === sorted.length - 1
              ? detectedGaps.find((g) => g.isJoiningGap)
              : null;
            const jKey        = joiningGap ? `${joiningGap.gapStart}_${joiningGap.gapEnd}` : null;

            return (
              <React.Fragment key={entry.id}>
                <div className="px-5 py-4 flex items-start gap-3">
                  <div className={`mt-1 w-3 h-3 rounded-full shrink-0 ${
                    Number(entry.is_current) === 1 ? 'bg-green-400 ring-2 ring-green-200' : 'bg-slate-300'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 text-sm">{entry.technology}</p>
                        <p className="text-sm text-slate-600">{entry.role}</p>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                          {entry.client && <span className="text-xs text-slate-400">Client: {entry.client}</span>}
                          {entry.vendor && <span className="text-xs text-slate-400">Vendor: {entry.vendor}</span>}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          {fmtDate(entry.start_date)} → {entry.end_date ? fmtDate(entry.end_date) : 'Present'}
                          {Number(entry.is_current) === 1 && <span className="ml-1.5 text-green-600 font-medium">(Current)</span>}
                        </p>
                        {entry.notes && <p className="text-xs text-slate-400 italic mt-0.5">{entry.notes}</p>}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => startEdit(entry)}
                          className="text-xs text-blue-500 hover:text-blue-700 border border-blue-200 hover:bg-blue-50 px-2.5 py-1 rounded-lg">
                          Edit
                        </button>
                        <button onClick={() => setDeleteTarget(entry.id)}
                          className="text-xs text-red-500 hover:text-red-700 border border-red-200 hover:bg-red-50 px-2.5 py-1 rounded-lg">
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Inter-entry gap */}
                {gapAfter && (
                  <GapRow
                    gapStart={gapAfter.gapStart}
                    gapEnd={gapAfter.gapEnd}
                    savedReason={savedGapReason(gapAfter.gapStart, gapAfter.gapEnd)}
                    reason={gapForms[gKey] ?? savedGapReason(gapAfter.gapStart, gapAfter.gapEnd)}
                    saving={!!gapSaving[gKey]}
                    onChange={(v) => setGapForms((p) => ({ ...p, [gKey]: v }))}
                    onSave={() => saveGapReason(gapAfter.gapStart, gapAfter.gapEnd)}
                  />
                )}

                {/* Joining-date gap (shown after the oldest entry) */}
                {joiningGap && (
                  <GapRow
                    gapStart={joiningGap.gapStart}
                    gapEnd={joiningGap.gapEnd}
                    label="Gap from joining date"
                    savedReason={savedGapReason(joiningGap.gapStart, joiningGap.gapEnd)}
                    reason={gapForms[jKey] ?? savedGapReason(joiningGap.gapStart, joiningGap.gapEnd)}
                    saving={!!gapSaving[jKey]}
                    onChange={(v) => setGapForms((p) => ({ ...p, [jKey]: v }))}
                    onSave={() => saveGapReason(joiningGap.gapStart, joiningGap.gapEnd)}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="border-t border-red-100 bg-red-50 px-5 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <p className="text-sm text-red-700 flex-1">Permanently delete this timeline entry?</p>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}
              className="border border-slate-300 text-slate-600 px-3 py-1.5 rounded-lg text-xs hover:bg-white">
              Cancel
            </button>
            <button onClick={handleDelete} disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1">
              {deleteLoading
                ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />…</>
                : 'Yes, Delete'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Gap Row ─────────────────────────────────────────────────────────────────── */
function GapRow({ gapStart, gapEnd, label, savedReason, reason, saving, onChange, onSave }) {
  const [editing, setEditing] = useState(!savedReason);
  const gapLabel = label || 'Gap';

  if (savedReason && !editing) {
    return (
      <div className="mx-5 my-1 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 flex items-start gap-3">
        <span className="text-green-500 mt-0.5">✓</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-400">{gapLabel}: {fmtDate(gapStart)} → {fmtDate(gapEnd)}</p>
          <p className="text-xs text-slate-700 font-medium">{savedReason}</p>
        </div>
        <button onClick={() => setEditing(true)} className="text-xs text-slate-400 hover:text-slate-600 shrink-0">Edit</button>
      </div>
    );
  }

  return (
    <div className="mx-5 my-1 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-orange-500">⚠</span>
        <p className="text-xs font-semibold text-orange-700">
          {gapLabel}: {fmtDate(gapStart)} → {fmtDate(gapEnd)}
        </p>
      </div>
      <textarea
        value={reason || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        placeholder="Explain the gap (between projects, personal leave…)"
        className="input-base text-xs py-1.5 resize-none w-full"
      />
      <div className="flex gap-2">
        {savedReason && (
          <button type="button" onClick={() => setEditing(false)}
            className="flex-1 border border-slate-300 text-slate-600 py-1.5 rounded-lg text-xs hover:bg-white">
            Cancel
          </button>
        )}
        <button type="button" onClick={onSave} disabled={saving || !reason?.trim()}
          className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1">
          {saving
            ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />…</>
            : 'Save Reason'}
        </button>
      </div>
    </div>
  );
}

/* ── Helper ──────────────────────────────────────────────────────────────────── */
function TLField({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
